import { select, Store } from '@ngrx/store';
import { State } from '@app/store/state/state';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Dictionary } from '@ngrx/entity';
import { NavigationExtras } from '@angular/router';

import {
  routerStateAction, navigateToLastRouteAction, fetchMessageAction,
  historyPushAction, historyPopAction, historyResetAction, setTrackingAction,
  resetCloseWebViewInDetailAction, initAssetsAction, setHistoryToSessionStorageAction, setCloseWebviewModal,
  addNavigationItemAction, removeLastNavigationItemAction, resetNavigationAction
} from '@app/store/actions/global.actions';
import { Airport } from '@app/modules/core/interfaces/airport';
import { selectAirportsEntities, selectAllAirports } from '@app/store/selectors/airports.selector';
import { Place } from '@app/entities/place';
import { selectAllPlaces, selectPlacesEntities } from '@app/store/selectors/places.selector';
import { selectAirlinesEntities } from '@app/store/selectors/airlines.selector';
import { Airline } from '@app/entities/airline';
import { UserInfo } from '@app/modules/core/interfaces/user';
import { Toastr } from '@app/entities/toastr';
import { UserAddresses } from '@app/modules/core/interfaces/address';
import { selectBusCompaniesEntities } from '@app/store/selectors/bus-companies.selector';
import { selectBusTerminalsEntities } from '@app/store/selectors/bus-terminals.selector';
import { RouterGlobalState, HistoryItem, NavigationItem } from '@app/entities/router-state.entity';
import { AirlinesWidget } from '@app/entities/flight-results-widget';
import { SearchBarState } from '@shared/base-search-bar/store/state/search-bar.state';
import { resetWidgetDataAction } from '@shared/widgets/store/actions/alerts.action';
import { TrackingState } from '@app/store/reducers/tracking.reducer';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { ProvincesByCity } from '@app/entities/provinces-cities';
import { DocumentAssetState } from '@app/entities/booking-documents';
import { BusTerminal } from '@app/entities/bus-company';
import { HeaderPageType } from '@header/entities/ds-header-config';

@Injectable()
export class AppFacade {

  constructor(protected store: Store<State>) { }

  get frequentFlyerPrograms$(): Observable<FrequentFlyerResponse[]> {
    return this.store.pipe(map(state => state?.frequentFlyerProgram));
  }

  get provinces$(): Observable<ProvincesByCity[]> {
    return this.store.pipe(map(state => state?.provinces));
  }

  get placesEntities$(): Observable<Dictionary<Place>> {
    return this.store.pipe(select(selectPlacesEntities));
  }

  get places$(): Observable<Place[]> {
    return this.store.pipe(select(selectAllPlaces));
  }

  get airports$(): Observable<Airport[]> {
    return this.store.pipe(select(selectAllAirports));
  }

  get airportsEntities$(): Observable<Dictionary<Airport>> {
    return this.store.pipe(select(selectAirportsEntities));
  }

  get terminalsEntities$(): Observable<Dictionary<BusTerminal>> {
    return this.store.pipe(select(selectBusTerminalsEntities));
  }

  public isLoggedIn$: Observable<boolean> = this.store.pipe(select(x => x.isLoggedIn));
  
  get closeWebViewInDetail$(): Observable<boolean> {
    return this.store.pipe(select(x => x.closeWebViewInDetail));
  }

  get trackingUtm$(): Observable<TrackingState> {
    return this.store.pipe(select(x => x.tracking));
  }

  get user$(): Observable<UserInfo> {
    return this.store.pipe(select(x => x.user));
  }

  get userAddresses$(): Observable<UserAddresses> {
    return this.store.pipe(select(x => x.userAddresses));
  }

  get routerState$(): Observable<RouterGlobalState> {
    return this.store.pipe(select(x => x.routerState));
  }

  get history$(): Observable<HistoryItem[]> {
    return this.store.pipe(select(state => state.history));
  }

  get seamlessLoginToken$(): Observable<string> {
    return this.store.pipe(select(state => state.seamlessLoginToken));
  }

  get airlineEntities$(): Observable<Dictionary<Airline>> {
    return this.store.pipe(select(selectAirlinesEntities));
  }

  get busCompaniesEntities$(): Observable<Dictionary<Airline>> {
    return this.store.pipe(select(selectBusCompaniesEntities));
  }

  get airlinesResultsWidgets$(): Observable<AirlinesWidget[]> {
    return this.store.pipe(
      select(state => state.flightResultsModuleState.flightResultsWidgets.Airlines)
    );
  }

  get infoFromSearch$(): Observable<{}> {
    return this.store.pipe(
      select(state => {
        return {
          'origin': state.baseSearchBarModuleState.departurePlace?.code,
          'destination': state.baseSearchBarModuleState.arrivalPlace?.code
        };
      })
    );
  }

  get searchData$(): Observable<SearchBarState> {
    return this.store.pipe(select(state => state.baseSearchBarModuleState));
  }

  get previousUrl$(): Observable<Object[]> {
    return this.store.select((state) => state.routerState.previousUrl.commands).pipe(take(1));
  }

  get flightSearchId$(): Observable<string> {
    return this.store.pipe(map(x => x?.flightResultsModuleState?.searchPayload?.Results?.SearchId));
  }

  get busSearchId$(): Observable<string> {
    return this.store.pipe(map(x => x.busesResultsModuleState?.searchPayload?.SearchId));
  }

  get hotelSearchId$(): Observable<string> {
    return this.store.pipe(map(x => x?.hotelResultsModuleState?.hotelResults?.SearchId));
  }

  get packageSearchId$(): Observable<string> {
    return this.store.pipe(map(state => state.packageResultModuleState?.packageSearchData?.SearchId));
  }

  get activitiesSearchId$(): Observable<string> {
    return this.store.pipe(map(state => state.activitiesResultsModuleState?.activitiesResults?.SearchId));
  }

  get autosSearchId$(): Observable<string> {
    return this.store.pipe(map(x => x?.autoResultsModuleState?.autoFiltersPayload?.SearchId));
  }
  
  get toastr$(): Observable<string> {
    return this.store.pipe(select(x => x.toastr));
  }

  get pageType$(): Observable<HeaderPageType> {
    return this.store.pipe(select(state => state?.headerModuleState?.dsHeaderConfig?.pageType));
  }

  get showCloseWebviewModal$(): Observable<boolean> {
    return this.store.pipe(select(state => state.setCloseWebviewModal));
  }

  public documentAssets$: Observable<DocumentAssetState> = this.store.pipe(map(state => state?.documentAssets));

  public isPrime$: Observable<boolean> = this.user$
    .pipe(map(user => !!user && !!user.is_prime));

  public hasRappiCard$: Observable<boolean> = this.user$
    .pipe(map(user => !!user && !!user.has_rappicard));

  public hasRappiAccount$: Observable<boolean> = this.user$
    .pipe(map(user => !!user && !!user.has_rappiaccount));

  public isAnInternationalSearch$: Observable<boolean> = this.store.pipe(map((state) => !!state?.isAnInternationalSearch));

  public isTokenAuthenticate$: Observable<boolean> = this.store.pipe(map((state) => !!state?.isTokenAuthenticate));

  public initAssets(): void {
    return this.store.dispatch(initAssetsAction());
  }

  public fetchToastrMessage(toastr: Toastr): void {
    this.store.dispatch(fetchMessageAction({ toastr }));
  }

  public setCurrentUrl(commands: Object[], extras: NavigationExtras = {}): void {
    this.store.dispatch(routerStateAction({ routerState: { commands, extras } }));
  }

  public historyPush(url: string, extras: NavigationExtras = {}): void {
    this.store.dispatch(historyPushAction({ item: { url, extras } }));
  }

  public setTrackingAction(tracking: TrackingState): void {
    this.store.dispatch(setTrackingAction({ tracking }));
  }

  public historyPop(): void {
    this.store.dispatch(historyPopAction());
  }

  public historyReset(): void {
    this.store.dispatch(historyResetAction());
  }

  public navigateToLastRoute(): void {
    this.store.dispatch(navigateToLastRouteAction());
  }

  public resetCloseWebViewInDetail(): void {
    this.store.dispatch(resetCloseWebViewInDetailAction());
  }

  public resetWidgetData(): void {
    this.store.dispatch(resetWidgetDataAction());
  }

  public setHistoryToSessionStorageAction(history: HistoryItem[]): void {
    return this.store.dispatch(setHistoryToSessionStorageAction({ payload: history }));
  }

  public setCloseWebviewModal(payload: boolean): void {
    this.store.dispatch(setCloseWebviewModal({ payload }))
  }

  public addNavigationItem(item: NavigationItem): void {
    this.store.dispatch(addNavigationItemAction({ item }));
  }

  public removeLastNavigationItem(): void {
    this.store.dispatch(removeLastNavigationItemAction());
  }

  public resetNavigation(): void {
    this.store.dispatch(resetNavigationAction());
  }

}
